'use client';

import { User } from '@/types';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { decodeJWTPayload, LOCAL_STORAGE_KEYS, secureStorage } from '@outmarket/utils';

let supabaseInstance: SupabaseClient | null = null;

// The session each client getSupabaseClient built was given, as sessionKey
// puts it.
const clientSessions = new WeakMap<SupabaseClient, string>();

const getStoredSupabaseUrl = (): string => {
  const connectedUrl = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CONNECTED_URL, true, '');
  const tenantId = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CONNECTED_TENANT_ID, true, '');
  return connectedUrl || `https://${tenantId}.supabase.co`;
};

/**
 * The tenant, user and GoTrue session an access token is for. A token rotation
 * keeps it; a sign-in, another user, an impersonation or another instance
 * changes it. A token without the sub and session_id claims stands for itself.
 */
const sessionKey = (supabaseUrl: string, accessToken: string): string => {
  const claims = decodeJWTPayload(accessToken);
  const session = claims?.sub && claims.session_id ? `${claims.sub}/${claims.session_id}` : accessToken;
  return `${supabaseUrl} ${session}`;
};

const getStoredSessionKey = (): string =>
  sessionKey(getStoredSupabaseUrl(), secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CLIENT_ACCESS_TOKEN, true, ''));

/**
 * True while `client` is still the shared client and storage still holds the
 * session whose `refreshToken` was sent to setSession. For an access token that
 * has not expired, setSession first asks GoTrue for the user, and while it waits
 * an instance switch, an impersonation start or stop, or an embed seed for
 * another user in this tab can replace the stored session. Writing the old
 * session back then would put its tokens under the new user's identity.
 * secureStorage reads its values from a copy it loads once per page, so another
 * tab's sign-in does not show up here; only its logout does (it removes the key).
 */
const isSessionStillCurrent = (client: SupabaseClient, refreshToken: string): boolean =>
  client === supabaseInstance &&
  secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CLIENT_REFRESH_TOKEN, true, '') === refreshToken;

/** Best-effort: close `client`'s realtime socket. Never throws. */
const disconnectRealtime = (client: SupabaseClient): void => {
  try {
    client.realtime?.disconnect?.();
  } catch {
    // Teardown is best effort; the caller has already stopped sharing `client`.
  }
};

/**
 * For a client that is no longer the shared one. Its realtime socket carries the
 * session setSession put on it (supabase-js sets realtime auth on SIGNED_IN).
 * When storage now holds another session, the socket is closed so its channels
 * do not stay live as that session. When storage holds the same session, the
 * socket stays open: code that kept the client (useCollaborationShareSync,
 * useConversationSync, the realtime provider's context value) is not told it was
 * replaced, and closing the socket would end its channels without a word. A
 * sign-in replaces the client twice for one session, once from
 * SupabaseRealtimeProvider and once from useLogin.
 */
const releaseReplacedClient = (client: SupabaseClient): void => {
  if (clientSessions.get(client) === getStoredSessionKey()) return;
  disconnectRealtime(client);
};

/** Stores the tokens setSession returned, when they differ from the ones sent. */
const storeRotatedTokens = (
  session: { access_token: string; refresh_token: string },
  sentAccessToken: string,
  sentRefreshToken: string,
): void => {
  if (session.access_token === sentAccessToken && session.refresh_token === sentRefreshToken) return;
  secureStorage.setItem(LOCAL_STORAGE_KEYS.CLIENT_ACCESS_TOKEN, session.access_token, true);
  secureStorage.setItem(LOCAL_STORAGE_KEYS.CLIENT_REFRESH_TOKEN, session.refresh_token, true);
};

/**
 * True when the tenant connection credentials required to build the LOCAL
 * (tenant) Supabase client are present in localStorage — i.e. the user has
 * logged in and connected to an instance.
 *
 * Callers use this to avoid invoking ``getSupabaseClient`` in the
 * unauthenticated / pre-connection window (e.g. sitting on the sign-in page, or
 * the first paint of a reload before the connection keys have landed), where it
 * would otherwise throw. Mirrors the guard inside ``getSupabaseClient`` exactly
 * so the two cannot drift.
 */
export const hasSupabaseCredentials = (): boolean => {
  const user = secureStorage.getItem<User | null>(LOCAL_STORAGE_KEYS.USER, true, null);
  const connectedUrl = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CONNECTED_URL, true, '');
  const tenantId = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CONNECTED_TENANT_ID, true, '');
  const publicKey = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CONNECTED_PUBLIC_KEY, true, '');
  return Boolean((connectedUrl || tenantId) && publicKey && user);
};

export const getSupabaseClient = async (forceNew = false) => {
  if (supabaseInstance && !forceNew) return supabaseInstance;

  // Clear existing instance if forcing new connection
  if (forceNew && supabaseInstance) {
    // IMPORTANT!! IMPORTANT!!
    // Farhan K: Please only use local scope to logout. Using a global scope will abruptly wipe out
    //           session for this user across all apps. Which means all cookie and header with token
    //           will become stale and no longer be able to renew session anymore. Handle with care
    // await supabaseInstance.auth.signOut({ scope: 'local' });
    const replaced = supabaseInstance;
    supabaseInstance = null;
    releaseReplacedClient(replaced);
  }

  if (!hasSupabaseCredentials()) {
    throw new Error('Supabase URL or Public Key not found in localStorage');
  }

  const publicKey = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CONNECTED_PUBLIC_KEY, true, '');
  const clientAccessToken = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CLIENT_ACCESS_TOKEN, true, '');
  const clientRefreshToken = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CLIENT_REFRESH_TOKEN, true, '');

  const supabaseUrl = getStoredSupabaseUrl();

  // Create client without Authorization header to allow proper session management
  const client = createClient(supabaseUrl, publicKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
    // TODO: Uncomment this when we have a way to handle the session properly.
    // global: {
    //   headers: {
    //     Authorization: `Bearer ${clientAccessToken}`,
    //   },
    // },
  });
  supabaseInstance = client;
  clientSessions.set(client, sessionKey(supabaseUrl, clientAccessToken));

  // If we have both access and refresh tokens, set the session properly
  // This ensures realtime connections and RLS policies work correctly
  // Opportunities and Vault realtime breaks when we dont use setSession. Needs more investigation.
  if (clientAccessToken && clientRefreshToken) {
    try {
      const { error, data } = await client.auth.setSession({
        access_token: clientAccessToken,
        refresh_token: clientRefreshToken,
      });

      if (error) {
        console.warn('Failed to set Supabase session:', error);
        // Continue without session - some functionality may be limited
      }
      if (data?.session && isSessionStillCurrent(client, clientRefreshToken)) {
        storeRotatedTokens(data.session, clientAccessToken, clientRefreshToken);
      }
    } catch (error) {
      console.warn('Error setting Supabase session:', error);
      // Continue without session - some functionality may be limited
    }
    // A newer client replaced this one while setSession waited, and setSession
    // may have just re-authenticated this one's realtime. A client that is still
    // the shared one keeps the session it was built with until the flow that
    // changed storage calls getSupabaseClient(true), which every user switch does.
    if (client !== supabaseInstance) releaseReplacedClient(client);
  }

  return supabaseInstance;
};

/**
 * Refresh the SHARED Supabase client's auth IN PLACE on a mid-session token
 * rotation — without recreating the client.
 *
 * Root cause of issue #21815: calling ``getSupabaseClient(true)`` on every
 * token refresh recreates the whole singleton (new realtime socket + new
 * GoTrueClient). That tears down the live realtime socket and orphans any
 * in-flight channel (e.g. the asset-upload channel), which then sits
 * ``channelState:'closed'`` / ``lastStatus:'INITIAL'`` and only recovers ~60s
 * later via supabaseRealtime's inactivity retry — and also triggers the
 * "Multiple GoTrueClient instances detected" warning. Updating the session +
 * realtime auth in place keeps the socket and channels alive.
 *
 * Reserve ``getSupabaseClient(true)`` (forceNew) for genuine tenant / URL / key
 * switches (login, tenant change) — NOT for token refresh. Never throws, so it
 * can't break the token-refresh flow.
 */
export const refreshSupabaseClientAuth = async (): Promise<void> => {
  // No live client yet → just create one normally (no socket to preserve).
  if (!supabaseInstance) {
    await getSupabaseClient();
    return;
  }
  const client = supabaseInstance;

  try {
    const clientAccessToken = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CLIENT_ACCESS_TOKEN, true, '');
    const clientRefreshToken = secureStorage.getItem<string>(LOCAL_STORAGE_KEYS.CLIENT_REFRESH_TOKEN, true, '');

    // Without both tokens setSession can't rotate the session; bail without
    // disturbing the live client.
    if (!clientAccessToken || !clientRefreshToken) return;

    const { data, error } = await client.auth.setSession({
      access_token: clientAccessToken,
      refresh_token: clientRefreshToken,
    });

    // The stored session changed while setSession waited on GoTrue: this result
    // is for a session storage no longer holds, so it is not stored. setSession
    // has already put it on this client and its realtime socket (supabase-js
    // sets realtime auth on SIGNED_IN). If another client replaced this one,
    // releaseReplacedClient closes its socket unless storage holds the same
    // session. A client that is still the shared one keeps that session until
    // the flow that changed storage calls getSupabaseClient(true), which every
    // user switch does.
    if (!isSessionStillCurrent(client, clientRefreshToken)) {
      if (client !== supabaseInstance) releaseReplacedClient(client);
      return;
    }

    if (error) {
      console.warn('Failed to refresh Supabase client auth in place:', error);
      return;
    }

    // Persist the (possibly rotated) tokens, mirroring getSupabaseClient.
    if (data?.session) {
      storeRotatedTokens(data.session, clientAccessToken, clientRefreshToken);
    }

    // Propagate the new access token to the realtime layer so LIVE channels keep
    // auth WITHOUT a socket teardown. Optional-chained for SDK version safety.
    client.realtime?.setAuth?.(data?.session?.access_token ?? clientAccessToken);
  } catch (err) {
    console.warn('Failed to refresh Supabase client auth in place:', err);
    // Never throw — must not break the token-refresh flow.
  }
};

/**
 * Dispose the shared LOCAL client so no later caller can reuse a prior user's
 * session. Best-effort and never throws — safe to call during the logout /
 * unauthenticated transition.
 *
 * Nulling the module singleton is what stops the many ``getSupabaseClient()``
 * callers from receiving the stale authenticated instance at the early
 * ``if (supabaseInstance && !forceNew) return supabaseInstance`` return.
 * Previously that clearing happened as a side effect of ``getSupabaseClient(true)``
 * on every SupabaseRealtimeProvider effect run; the credential guard skips that
 * call in the unauthenticated window, so the provider must clear explicitly here.
 */
export const clearSupabaseInstance = (): void => {
  if (!supabaseInstance) return;
  // Best-effort socket teardown so the logged-out user's realtime connection
  // doesn't linger. It never throws: nulling the ref below is what actually
  // prevents reuse via getSupabaseClient().
  disconnectRealtime(supabaseInstance);
  supabaseInstance = null;
};

export const setSupabaseInstance = (instance: SupabaseClient) => {
  supabaseInstance = instance;
};

/**
 * Resolve the id of the currently signed-in user from the LOCAL Supabase session,
 * throwing if there is no session. Reads the JWT `sub` from `getSession()` — no
 * server round-trip — which is the same id the AuthContext `user` carries. Used to
 * scope self-RLS writes (e.g. the `public.user_onboarding` upserts): the returned
 * id is both the row's `user_id` and matches the `auth.uid()` that RLS enforces
 * server-side, so the locally-read id is sufficient (we don't need `getUser()` to
 * revalidate the token).
 */
export const getAuthenticatedUserId = async (supabase: SupabaseClient): Promise<string> => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }
  const userId = data.session?.user?.id;
  if (!userId) {
    throw new Error('No authenticated user');
  }
  return userId;
};
